"use client"

import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

/**
 * Aligned with the hygenius-pria reference popover
 * (../hygenius-pria/src/components/ui/popover.tsx).
 *
 * What this replaced: a file pulled from a Tailwind-v4-era shadcn registry.
 * Its `outline-hidden` and `origin-(--radix-popover-content-transform-origin)`
 * are v4-only spellings that emit no CSS under www's Tailwind 3, so neither
 * did anything. `outline-hidden` becomes `outline-none`; the transform-origin
 * class is simply dropped, as the reference has none — the zoom-in animation
 * therefore scales from the content's own centre rather than from the trigger
 * anchor, which is what the reference does today.
 *
 * See dialog.tsx's header for why the parts are back on forwardRef.
 */

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
